import React, { useEffect, useMemo, useState } from 'react';
import {
    Table,
    Select,
    DatePicker,
    Input,
    Button,
    Space,
    Tag,
    Drawer,
    Descriptions,
    Empty,
    Divider,
    Typography,
    Spin,
} from 'antd';
import { EyeOutlined, ReloadOutlined, ClearOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { useAuditLog } from '../../store/auditLogContext';
import {
    AUDIT_OPERATIONS,
    AuditLogChange,
    AuditLogRecord,
    AuditOperation,
} from '../../types/auditLog';
import PageNavigation from '../Layout/PageNavigation';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const operationColors: Record<AuditOperation, string> = {
    create: 'green',
    update: 'blue',
    delete: 'red',
};

function formatDateTime(value?: string | null): string {
    return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
}

/** Audited values can be objects, arrays or primitives — render all of them readably. */
function formatValue(value: any): string {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
}

const AuditLogList: React.FC = () => {
    const { t } = useTranslation();
    const { logs, pagination, summary, loading, fetchLogs, fetchSummary, fetchLogById } = useAuditLog();

    const [models, setModels] = useState<string[]>([]);
    const [operations, setOperations] = useState<AuditOperation[]>([]);
    const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
    const [documentId, setDocumentId] = useState<string>('');
    const [documentIdInput, setDocumentIdInput] = useState<string>('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);

    const [selected, setSelected] = useState<AuditLogRecord | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    // Model name as stored on the log ("SellInvoice"), translated when a label exists.
    const modelLabel = (name: string) => t(`audit_model_${name}`, { defaultValue: name }) as string;

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    useEffect(() => {
        fetchLogs({
            collectionName: models,
            operation: operations,
            documentId: documentId || undefined,
            from: range?.[0] ? range[0].startOf('day').toISOString() : undefined,
            to: range?.[1] ? range[1].endOf('day').toISOString() : undefined,
            page,
            limit,
            sort: 'desc',
        });
    }, [models, operations, documentId, range, page, limit, fetchLogs]);

    const modelOptions = useMemo(
        () => summary.map(item => ({
            value: item.collectionName,
            label: `${modelLabel(item.collectionName)} (${item.total})`,
        })),
        [summary, t] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const operationOptions = useMemo(
        () => AUDIT_OPERATIONS.map(op => ({
            value: op,
            label: t(`audit_action_${op}`),
        })),
        [t]
    );

    const resetFilters = () => {
        setModels([]);
        setOperations([]);
        setRange(null);
        setDocumentId('');
        setDocumentIdInput('');
        setPage(1);
    };

    const refresh = () => {
        fetchSummary();
        fetchLogs({
            collectionName: models,
            operation: operations,
            documentId: documentId || undefined,
            from: range?.[0] ? range[0].startOf('day').toISOString() : undefined,
            to: range?.[1] ? range[1].endOf('day').toISOString() : undefined,
            page,
            limit,
            sort: 'desc',
        });
    };

    const openDrawer = async (record: AuditLogRecord) => {
        // The list response strips `lastState`, so the full log is fetched on demand.
        setSelected(record);
        setDrawerOpen(true);
        setDetailLoading(true);
        const full = await fetchLogById(record.id);
        if (full) setSelected(full);
        setDetailLoading(false);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelected(null);
    };

    const changeColumns = [
        { title: t('audit_field'), dataIndex: 'field', width: 200 },
        {
            title: t('audit_old_value'),
            dataIndex: 'oldValue',
            render: (value: any) => <Text type="secondary" delete={value !== undefined && value !== null}>{formatValue(value)}</Text>,
        },
        {
            title: t('audit_new_value'),
            dataIndex: 'newValue',
            render: (value: any) => <Text strong>{formatValue(value)}</Text>,
        },
    ];

    const columns = [
        {
            title: t('audit_time'),
            dataIndex: 'timestamp',
            render: (value: string) => formatDateTime(value),
        },
        {
            title: t('audit_model'),
            dataIndex: 'collectionName',
            render: (name: string) => <Tag color="geekblue">{modelLabel(name)}</Tag>,
        },
        {
            title: t('audit_action'),
            dataIndex: 'operation',
            render: (op: AuditOperation) => (
                <Tag color={operationColors[op] || 'default'}>{t(`audit_action_${op}`, { defaultValue: op })}</Tag>
            ),
        },
        {
            title: t('audit_user'),
            dataIndex: ['user', 'name'],
            render: (_: any, record: AuditLogRecord) => (
                <Space direction="vertical" size={0}>
                    <span>{record.user?.name || '-'}</span>
                    {record.user?.role && <Text type="secondary" style={{ fontSize: 12 }}>{record.user.role}</Text>}
                </Space>
            ),
        },
        {
            title: t('audit_record_id'),
            dataIndex: 'documentId',
            render: (id: string | null) => (
                <Text copyable={!!id} style={{ fontSize: 12 }}>{id || '-'}</Text>
            ),
        },
        {
            title: t('audit_changes'),
            dataIndex: 'changes',
            render: (changes?: AuditLogChange[]) =>
                changes?.length ? <Tag>{changes.length}</Tag> : <Text type="secondary">-</Text>,
        },
        {
            title: t('actions'),
            render: (_: any, record: AuditLogRecord) => (
                <Button icon={<EyeOutlined />} onClick={() => openDrawer(record)}>
                    {t('audit_details')}
                </Button>
            ),
        },
    ];

    const expandedRowRender = (record: AuditLogRecord) =>
        record.changes?.length ? (
            <Table
                rowKey={(change: AuditLogChange, index?: number) => `${record.id}-${change.field}-${index}`}
                columns={changeColumns}
                dataSource={record.changes}
                pagination={false}
                size="small"
            />
        ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('audit_no_changes')} />
        );

    return (
        <div>
            <PageNavigation title={t('audit_logs') as string} />

            <Space wrap style={{ marginBottom: 16 }} size="middle">
                <Select
                    mode="multiple"
                    allowClear
                    style={{ minWidth: 240 }}
                    placeholder={t('audit_all_models')}
                    value={models}
                    options={modelOptions}
                    onChange={values => { setModels(values); setPage(1); }}
                    maxTagCount="responsive"
                />
                <Select
                    mode="multiple"
                    allowClear
                    style={{ minWidth: 200 }}
                    placeholder={t('audit_all_actions')}
                    value={operations}
                    options={operationOptions}
                    onChange={values => { setOperations(values); setPage(1); }}
                    maxTagCount="responsive"
                />
                <RangePicker
                    value={range}
                    onChange={dates => {
                        setRange(dates && dates[0] && dates[1] ? [dates[0], dates[1]] : null);
                        setPage(1);
                    }}
                />
                <Input.Search
                    allowClear
                    style={{ width: 260 }}
                    placeholder={t('audit_record_id_ph') as string}
                    value={documentIdInput}
                    onChange={e => setDocumentIdInput(e.target.value)}
                    onSearch={value => { setDocumentId(value.trim()); setPage(1); }}
                />
                <Button icon={<ClearOutlined />} onClick={resetFilters}>
                    {t('audit_reset_filters')}
                </Button>
                <Button icon={<ReloadOutlined />} onClick={refresh}>
                    {t('audit_refresh')}
                </Button>
            </Space>

            <Table
                rowKey="id"
                loading={loading}
                dataSource={logs}
                columns={columns}
                expandable={{ expandedRowRender }}
                scroll={{ x: 'max-content' }}
                pagination={{
                    current: pagination.page,
                    pageSize: pagination.limit,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '25', '50', '100'],
                    showTotal: total => `${t('audit_total_logs')}: ${total}`,
                    onChange: (nextPage, nextSize) => {
                        setPage(nextSize !== limit ? 1 : nextPage);
                        setLimit(nextSize);
                    },
                }}
                locale={{ emptyText: <Empty description={t('audit_no_logs')} /> }}
            />

            <Drawer
                title={selected ? `${modelLabel(selected.collectionName)} — ${t(`audit_action_${selected.operation}`, { defaultValue: selected.operation })}` : t('audit_details')}
                placement="right"
                width={560}
                open={drawerOpen}
                onClose={closeDrawer}
            >
                <Spin spinning={detailLoading}>
                    {selected ? (
                        <>
                            <Descriptions bordered size="small" column={1}>
                                <Descriptions.Item label={t('audit_time')}>
                                    {formatDateTime(selected.timestamp)}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('audit_model')}>
                                    <Tag color="geekblue">{modelLabel(selected.collectionName)}</Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('audit_action')}>
                                    <Tag color={operationColors[selected.operation] || 'default'}>
                                        {t(`audit_action_${selected.operation}`, { defaultValue: selected.operation })}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('audit_record_id')}>
                                    <Text copyable={!!selected.documentId}>{selected.documentId || '-'}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('audit_user')}>
                                    {selected.user?.name || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('audit_role')}>
                                    {selected.user?.role || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('email')}>
                                    {selected.user?.email || '-'}
                                </Descriptions.Item>
                            </Descriptions>

                            <Divider orientation="left" style={{ marginTop: 20 }}>
                                {t('audit_changes')}
                            </Divider>
                            {selected.changes?.length ? (
                                <Table
                                    rowKey={(change: AuditLogChange, index?: number) => `${change.field}-${index}`}
                                    columns={changeColumns}
                                    dataSource={selected.changes}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: 'max-content' }}
                                />
                            ) : (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('audit_no_changes')} />
                            )}

                            {selected.lastState !== undefined && selected.lastState !== null && (
                                <>
                                    <Divider orientation="left" style={{ marginTop: 20 }}>
                                        {t('audit_last_state')}
                                    </Divider>
                                    <pre
                                        style={{
                                            background: '#f5f5f5',
                                            padding: 12,
                                            borderRadius: 8,
                                            maxHeight: 320,
                                            overflow: 'auto',
                                            fontSize: 12,
                                            direction: 'ltr',
                                            textAlign: 'left',
                                        }}
                                    >
                                        {JSON.stringify(selected.lastState, null, 2)}
                                    </pre>
                                </>
                            )}
                        </>
                    ) : (
                        <Empty description={t('audit_no_selected')} />
                    )}
                </Spin>
            </Drawer>
        </div>
    );
};

export default AuditLogList;
